import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  LifeBuoy, Plus, Send, MessageSquare, ArrowRight, Star, Phone, Mail, Clock,
  CheckCircle2, X,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Field, Empty, Rows, Badge } from '../components/UI'
import { get, post } from '../lib/api'
import { toast } from '../store'
import { clsx, faDate, timeAgo, asArray, num } from '../lib/format'

const STATUS_KIND: Record<string, 'up' | 'down' | 'info' | 'gold' | 'muted'> = {
  open: 'gold',
  answered: 'up',
  pending: 'info',
  closed: 'muted',
}

function Stars({ value, onPick }: { value: number; onPick?: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onPick}
          onClick={() => onPick?.(n)}
          className={clsx('transition', onPick && 'hover:scale-110')}
          aria-label={`${n} ستاره`}
        >
          <Star className={clsx('h-5 w-5', n <= value ? 'fill-gold text-gold' : 'text-slate-600')} />
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ گفتگو */
function Thread({ id, onBack, onChanged }: { id: number; onBack: () => void; onChanged: () => void }) {
  const [data, setData] = useState<any>(null)
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [rating, setRating] = useState(0)
  const endRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    get(`/user/tickets/${id}`)
      .then((r) => { setData(r); setRating(r.rating || 0) })
      .catch((e: any) => { toast.err(e.message); onBack() })
  }, [id, onBack])

  useEffect(() => { setData(null); load() }, [id, load])
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }) }, [data?.messages?.length])

  const send = async () => {
    const text = body.trim()
    if (text.length < 2) return
    setSending(true)
    try {
      await post(`/user/tickets/${id}/reply`, { body: text })
      setBody('')
      load()
      onChanged()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setSending(false)
    }
  }

  const close = async () => {
    try {
      await post(`/user/tickets/${id}/close`, {})
      toast.ok('تیکت بسته شد')
      load()
      onChanged()
    } catch (e: any) { toast.err(e.message) }
  }

  const rate = async (n: number) => {
    setRating(n)
    try {
      await post(`/user/tickets/${id}/rate`, { rating: n })
      toast.ok('از بازخورد شما ممنونیم')
      onChanged()
    } catch (e: any) { toast.err(e.message) }
  }

  if (!data) return <Rows n={5} h="h-16" />

  const messages = asArray<any>(data.messages)
  const closed = data.status === 'closed'

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="btn-ghost !px-3 text-xs">
        <ArrowRight className="h-4 w-4" /> بازگشت به فهرست تیکت‌ها
      </button>

      <Card>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
          <div className="min-w-0">
            <h2 className="mb-1.5 font-bold text-white">{data.subject}</h2>
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span>تیکت #{num(data.id)}</span>
              <span>·</span>
              <span>{data.department}</span>
              <span>·</span>
              <span>اولویت {data.priorityLabel}</span>
              <span>·</span>
              <span>{faDate(data.createdAt)}</span>
            </div>
          </div>
          <Badge kind={STATUS_KIND[data.status] || 'info'}>{data.statusLabel}</Badge>
        </div>

        {/* پیام‌ها */}
        <div className="space-y-3">
          {messages.map((m: any, i: number) => (
            <div key={m.id ?? i} className={clsx('flex', m.isStaff ? 'justify-end' : 'justify-start')}>
              <div
                className={clsx(
                  'max-w-[85%] rounded-2xl px-4 py-3',
                  m.isStaff
                    ? 'border border-brand-500/25 bg-brand-500/10'
                    : 'border border-white/10 bg-white/[.04]',
                )}
              >
                <div className="mb-1.5 flex items-center gap-2 text-[11px]">
                  <span className={clsx('font-semibold', m.isStaff ? 'text-brand-200' : 'text-slate-300')}>
                    {m.isStaff ? 'پشتیبانی ارزینو' : 'شما'}
                  </span>
                  <span className="text-slate-500">{timeAgo(m.createdAt)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-8 text-slate-300">{m.body}</p>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {/* پاسخ */}
        <div className="mt-5 border-t border-white/10 pt-4">
          {closed ? (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                این تیکت بسته شده است. اگر پیام تازه‌ای بفرستید، دوباره باز می‌شود.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-400">پاسخ پشتیبانی چقدر مفید بود؟</span>
                <Stars value={rating} onPick={data.rating ? undefined : rate} />
              </div>
            </div>
          ) : null}

          <textarea
            className="input mt-3 min-h-[7rem] w-full resize-y leading-8"
            placeholder="پاسخ خود را بنویسید…"
            value={body}
            maxLength={4000}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={send} loading={sending} disabled={body.trim().length < 2}>
              <Send className="h-4 w-4" /> ارسال پاسخ
            </Button>
            {!closed && (
              <button onClick={close} className="btn-ghost !px-4 text-xs">
                <CheckCircle2 className="h-4 w-4" /> مشکل حل شد، تیکت را ببند
              </button>
            )}
            <span className="mr-auto text-[11px] text-slate-600">{num(body.length)} / ۴۰۰۰</span>
          </div>
        </div>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ صفحه */
export default function Support() {
  const [params, setParams] = useSearchParams()
  const opened = params.get('ticket')
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>({ departments: [], priorities: [], contact: {} })
  const [open, setOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [department, setDepartment] = useState('پشتیبانی')
  const [priority, setPriority] = useState('normal')
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    get('/user/tickets')
      .then((r) => setRows(asArray(r, 'data')))
      .catch(() => setRows([]))
  }, [])

  useEffect(() => {
    load()
    get('/user/support/meta').then(setMeta).catch(() => undefined)
  }, [load])

  const create = async () => {
    setLoading(true)
    try {
      const r: any = await post('/user/tickets', { subject, body, department, priority })
      toast.ok(r?.message || 'تیکت ثبت شد')
      setOpen(false); setSubject(''); setBody('')
      load()
      setParams({ ticket: String(r.id) })
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setLoading(false)
    }
  }

  const contact = meta.contact || {}
  const departments: string[] = asArray<string>(meta.departments)
  const priorities = asArray<any>(meta.priorities)

  if (opened) {
    return (
      <PanelShell title="پشتیبانی" subtitle="گفتگو با کارشناسان ارزینو">
        <Thread
          id={Number(opened)}
          onBack={() => { setParams({}); load() }}
          onChanged={load}
        />
      </PanelShell>
    )
  }

  return (
    <PanelShell title="پشتیبانی" subtitle="سؤال یا مشکلی دارید؟ تیکت بزنید تا کارشناسان ما بررسی کنند">
      {/* راه‌های تماس */}
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        {contact.phone && (
          <Card className="flex items-center gap-3 !py-4">
            <Phone className="h-5 w-5 shrink-0 text-brand-300" />
            <div className="min-w-0">
              <div className="text-[11px] text-slate-500">تلفن پشتیبانی</div>
              <div className="truncate text-sm font-semibold text-white">{contact.phone}</div>
            </div>
          </Card>
        )}
        {contact.email && (
          <Card className="flex items-center gap-3 !py-4">
            <Mail className="h-5 w-5 shrink-0 text-brand-300" />
            <div className="min-w-0">
              <div className="text-[11px] text-slate-500">ایمیل</div>
              <div className="truncate text-sm font-semibold text-white" dir="ltr">{contact.email}</div>
            </div>
          </Card>
        )}
        <Card className="flex items-center gap-3 !py-4">
          <Clock className="h-5 w-5 shrink-0 text-brand-300" />
          <div className="min-w-0">
            <div className="text-[11px] text-slate-500">ساعات پاسخگویی</div>
            <div className="truncate text-sm font-semibold text-white">{contact.hours || '۲۴ ساعته'}</div>
          </div>
        </Card>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          پیش از ثبت تیکت، بخش <a href="/faq" className="text-brand-300 hover:underline">پرسش‌های متداول</a> را
          ببینید — پاسخ بیشتر سؤال‌ها همان‌جاست.
        </p>
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> تیکت جدید</Button>
      </div>

      {/* فهرست تیکت‌ها */}
      {rows === null ? (
        <Rows n={4} h="h-20" />
      ) : rows.length === 0 ? (
        <Card><Empty text="هنوز تیکتی ثبت نکرده‌اید" icon={<LifeBuoy className="h-6 w-6" />} /></Card>
      ) : (
        <div className="space-y-3">
          {rows.map((t: any) => (
            <button
              key={t.id}
              onClick={() => setParams({ ticket: String(t.id) })}
              className="w-full text-right"
            >
              <Card hover className="flex flex-wrap items-center gap-3">
                <MessageSquare className="h-5 w-5 shrink-0 text-slate-500" />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="truncate font-semibold text-white">{t.subject}</span>
                    {t.unread > 0 && (
                      <span className="rounded-full bg-down px-1.5 text-[10px] font-bold text-white">
                        {num(t.unread)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    <span>#{num(t.id)}</span>
                    <span>·</span>
                    <span>{t.department}</span>
                    <span>·</span>
                    <span>آخرین پیام {timeAgo(t.lastReplyAt)}</span>
                    {t.rating ? (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-gold">
                          <Star className="h-3 w-3 fill-gold" /> {num(t.rating)}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Badge kind={STATUS_KIND[t.status] || 'info'}>{t.statusLabel}</Badge>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* تیکت جدید */}
      <Modal open={open} onClose={() => setOpen(false)} title="ثبت تیکت جدید">
        <Field label="دپارتمان" hint="دپارتمان درست، پاسخ را سریع‌تر می‌کند">
          <select className="input w-full" value={department} onChange={(e) => setDepartment(e.target.value)}>
            {(departments.length ? departments : ['پشتیبانی']).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </Field>

        <Field label="اولویت">
          <div className="flex gap-2">
            {(priorities.length ? priorities : [{ value: 'normal', label: 'عادی' }]).map((p: any) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPriority(p.value)}
                className={clsx(
                  'flex-1 rounded-xl border px-3 py-2 text-xs transition',
                  priority === p.value
                    ? 'border-brand-500/50 bg-brand-500/10 text-brand-200'
                    : 'border-white/10 text-slate-400 hover:text-slate-200',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="موضوع">
          <input
            className="input w-full"
            value={subject}
            maxLength={140}
            placeholder="مثلاً: واریز تتر من ثبت نشده است"
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>

        <Field
          label="شرح مشکل"
          hint="هرچه دقیق‌تر بنویسید — شناسه تراکنش، شبکه، مبلغ و زمان — سریع‌تر حل می‌شود"
        >
          <textarea
            className="input min-h-[9rem] w-full resize-y leading-8"
            value={body}
            maxLength={4000}
            placeholder="توضیح کامل مشکل…"
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>

        <div className="flex items-center gap-2">
          <Button onClick={create} loading={loading}
                  disabled={subject.trim().length < 3 || body.trim().length < 10}>
            <Send className="h-4 w-4" /> ثبت تیکت
          </Button>
          <button className="btn-ghost !px-4 text-xs" onClick={() => setOpen(false)}>
            <X className="h-4 w-4" /> انصراف
          </button>
        </div>
      </Modal>
    </PanelShell>
  )
}
